import React from 'react';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { LabelWrapper } from '~src/catalog/CatalogLabelsHookHelper';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/labels/DataQualityType';
import { Messages } from '~src/codegen/Localize';
import { DraftTags } from '~src/drafts/components/DraftTags';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { DraftTagsParentType } from '~src/drafts/types/DraftsTypes';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { CardPopoverWidget, ContentCard, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { Thumbnail } from '~src/spacesPlatform/components/card/Thumbnail';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';
import { cssClass } from '~src/style';

import { PublishedConnectionTooltip } from '../tooltips/PublishedConnectionTooltip';

const certificationCardStyle: React.CSSProperties = {
  paddingTop: '3px',
  marginLeft: '-9px',
  marginRight: '-3px',
};

const draftStatusClass = cssClass('PublishedConnectionCard', 'draftStatus', {
  display: 'flex',
  marginBottom: CommonStyles.Sizes.SmallSpace,
});

const publishedConnectionEditorPrefix = '/published-connection-editor/';

export class PublishedConnectionCard extends ContentCard {
  private renderDraftStatus(virtualConnection: IContentInfo<'virtualConnection'>) {
    return (
      <div className={draftStatusClass}>
        <DraftTags
          draftStatus={virtualConnection.draftStatus}
          parentType={DraftTagsParentType.card}
          explorableItemType={'virtualConnection'}
        />
      </div>
    );
  }

  public render() {
    const {
      content, urls, hovered, selected, allowDragAndDrop, addContentFavorite, removeContentFavorite
    } = this.props;
    const isUnpublishedVirtualConnectionDraft = content.draftStatus === ContentDraftStatus.unpublished;
    const shouldShowFavoritesButton: boolean = !isUnpublishedVirtualConnectionDraft;

    let navigateUrl = isUnpublishedVirtualConnectionDraft
      ? `${publishedConnectionEditorPrefix}?draft=${content.draftLuid}`
      : getContentUrlForItem(content, urls);
    if (isUnpublishedVirtualConnectionDraft) {
      // only owner should see the edit link
      const server = getSessionManagementServiceInstance();
      if (content.owner.id !== server.sessionUserId()) {
        navigateUrl = undefined;
      }
    }

    const imageUrl = PublishedConnections.getPublishedConnectionHeaderIconPath(content as ISpaceItemDetailInfo<'virtualConnection'>);
    const virtualConnection = content as IContentInfo<'virtualConnection'>;
    const certificationsExist = content.dataQualityIndicators?.filter(i => i.type === CERTIFIED_INDICATOR_TYPE)[0] ?? null;

    const certificationComponent = certificationsExist ?
        <LabelWrapper itemInfo={virtualConnection}
                      category={'certification'}
                      isEager={false}
                      kind={'icon'}
                      wrapper={(e) => <div data-tb-test-id='virtualConnection-card-certification' style={certificationCardStyle}>{e}</div>}/>: null;


    return (
      <div className={CardViewStyles.Card}>
        <Thumbnail
          defaultImageUrl={imageUrl}
          thumbnailImageUrl={content.thumbnailUrl}
          hovered={hovered}
          selected={selected}
          navigateUrl={navigateUrl}
          openLinkInNewTab={isUnpublishedVirtualConnectionDraft}
          useBase64EncodedImage={true}
          allowDragAndDrop={allowDragAndDrop}
          aria-label={content.name}
        />
        <div className={CardViewStyles.ActionRow}>
          {certificationComponent}
          <span data-tb-test-id="publishedConnection-name" className={CardViewStyles.ActionRowName} title={content.name}>
            {content.name}
          </span>
          <span>
            <CardPopoverWidget
              content={content}
              onTriggerClick={this.handleShowPopover}
              popoverContent={this.renderPopoverContent()}
              title={Messages.publishedConnectionTooltipTitle()}
            />
          </span>
          {shouldShowFavoritesButton &&
            <span>
              <FavoriteToggle
                isTabbable={true}
                isFavorite={content.favorite}
                onAddFavorite={() => addContentFavorite({ ...content, contentType: 'virtualConnection' })}
                onRemoveFavorite={() => removeContentFavorite({ ...content, contentType: 'virtualConnection' })}
              />
            </span>
          }
          {this.renderActionsMenu()}
        </div>
        {this.renderDraftStatus(virtualConnection)}
        <div className={CardViewStyles.CardDescription}>
          {this.getDescription()}
        </div>
      </div>
    );
  }

  private renderPopoverContent = () => <PublishedConnectionTooltip
    content={this.props.content}
    urls={this.props.urls}
    inProjectSpace={this.props.inProjectSpace}
  />;
}
